import { useEffect } from 'react';
import dayjs from 'dayjs';
import type { CalendarPosition } from '../CalendarNavigationHandler.types';

type UsePositionResetParams = {
  calendars: Record<string, { calendarId: string }>;
  startDate: string | null;
  timeSlotMin: Date;
  position: CalendarPosition | undefined;
  setPosition: (position?: CalendarPosition) => void;
};

export const usePositionReset = ({
  calendars,
  startDate,
  timeSlotMin,
  position,
  setPosition
}: UsePositionResetParams) => {
  'use memo';

  const positionCalendarId = position?.calendarId;

  const resetPosition = () => {
    const calendarValues = Object.values(calendars);

    if (calendarValues.length === 0 || !startDate || !timeSlotMin) {
      setPosition(undefined);
      return;
    }

    if (positionCalendarId) {
      const calendarOnScreen = calendarValues.some(
        ({ calendarId }) => calendarId === positionCalendarId
      );
      if (calendarOnScreen) return;
    }

    const calendarKeys = Object.keys(calendars).map(Number);
    const newestKey = Math.max(...calendarKeys);
    const targetCalendar = calendars[newestKey];

    const firstDateTime = dayjs(startDate)
      .set('hour', dayjs(timeSlotMin).hour())
      .set('minute', dayjs(timeSlotMin).minute());

    setPosition({
      calendarId: targetCalendar.calendarId,
      date: firstDateTime
    });
  };

  // Only re-run when the calendar config or date range changes — not on every
  // position update. The effect's job is to validate and reset the position when
  // the available calendars or viewing period change. If positionCalendarId were
  // included, the effect would fire on every navigation step, which is incorrect.
  // resetPosition is omitted because the React Compiler ('use memo') stabilizes
  // it automatically; the linter does not understand compiler-level memoization.
  useEffect(() => {
    resetPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendars, startDate, timeSlotMin]);

  return resetPosition;
};
